import React, { useEffect, useMemo, useState } from "react";
import { Global } from "../helpers/Global";
import useAuth from "../hooks/useAuth";

// ✅ Toast global
import { useToast } from "../hooks/useToast";

// ===== MUI =====
import { Box, Paper, Typography, TextField, Button, Stack } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";

export const CrearJornadaManual = () => {
  const { auth } = useAuth();
  const toast = useToast();

  const [usuarios, setUsuarios] = useState([]);
  const [centros, setCentros] = useState([]);

  const [usuarioId, setUsuarioId] = useState("");
  const [centroId, setCentroId] = useState("");
  const [inicio, setInicio] = useState("");
  const [fin, setFin] = useState("");
  const [comentario, setComentario] = useState("");

  const [loadingData, setLoadingData] = useState(true);
  const [saving, setSaving] = useState(false);

  const esAdmin = auth?.rol === "admin";
  const esGerente = auth?.rol === "gerente";

  useEffect(() => {
    if (!auth?.id) return;
    cargarCombos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.id]);

  const cargarCombos = async () => {
    setLoadingData(true);
    try {
      const [resUsuarios, resCentros] = await Promise.all([
        fetch(`${Global.url}usuarios`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
        fetch(`${Global.url}centros`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
      ]);

      if (resUsuarios.ok) setUsuarios(await resUsuarios.json());
      else toast.error("No se pudieron cargar los usuarios.");

      if (resCentros.ok) setCentros(await resCentros.json());
      else toast.error("No se pudieron cargar los centros.");
    } catch (err) {
      console.error("Error al cargar combos:", err);
      toast.error("Error de conexión al cargar usuarios/centros.");
    } finally {
      setLoadingData(false);
    }
  };

  const usuariosDisponibles = useMemo(() => {
    const base = Array.isArray(usuarios) ? usuarios : [];

    if (esAdmin) {
      return base
        .slice()
        .sort((a, b) =>
          `${a.nombre} ${a.apellidos || ""}`.trim().localeCompare(
            `${b.nombre} ${b.apellidos || ""}`.trim()
          )
        );
    }

    if (esGerente) {
      return base
        .filter((u) => String(u.gerente_id) === String(auth?.id))
        .sort((a, b) =>
          `${a.nombre} ${a.apellidos || ""}`.trim().localeCompare(
            `${b.nombre} ${b.apellidos || ""}`.trim()
          )
        );
    }

    return [];
  }, [usuarios, esAdmin, esGerente, auth?.id]);

  const centrosOrdenados = useMemo(() => {
    return (centros || []).slice().sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""));
  }, [centros]);

  const usuarioSeleccionado =
    usuariosDisponibles.find((u) => String(u.id) === String(usuarioId)) || null;

  const centroSeleccionado =
    centrosOrdenados.find((c) => String(c.id) === String(centroId)) || null;

  const limpiarFormulario = () => {
    setUsuarioId("");
    setCentroId("");
    setInicio("");
    setFin("");
    setComentario("");
    toast.info("Formulario limpiado.");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!usuarioId || !centroId || !inicio || !fin) {
      toast.warning("Usuario, centro, inicio y fin son obligatorios.");
      return;
    }

    const usuarioPermitido = usuariosDisponibles.some((u) => String(u.id) === String(usuarioId));
    if (!usuarioPermitido) {
      toast.error("No puedes crear jornadas manuales para ese usuario.");
      return;
    }

    const dIni = new Date(inicio);
    const dFin = new Date(fin);

    if (Number.isNaN(dIni.getTime()) || Number.isNaN(dFin.getTime()) || dFin < dIni) {
      toast.warning("La fecha de fin debe ser posterior al inicio.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        usuario_id: parseInt(usuarioId, 10),
        centro_id: parseInt(centroId, 10),
        inicio,
        fin,
        comentario,
        observaciones_admin: comentario,
      };

      const res = await fetch(`${Global.url}jornadas/manual`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        toast.success("Jornada creada correctamente.");
        limpiarFormulario();
        return;
      }

      const errorData = await res.json().catch(() => ({}));
      toast.error(errorData?.message || "No se pudo crear la jornada.");
    } catch (error) {
      console.error("Error al crear jornada:", error);
      toast.error("Error al conectar con el servidor.");
    } finally {
      setSaving(false);
    }
  };

  const titulo = esAdmin
    ? "Inserción Manual de Jornada"
    : esGerente
    ? "Crear Jornada Manual de mi equipo"
    : "Crear Jornada Manual";

  return (
    <Box className="appContainer">
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {titulo}
        </Typography>

        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <Paper
            sx={{
              p: 2,
              width: "100%",
              maxWidth: 900,
            }}
          >
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                alignItems: "start",
              }}
            >
              <Autocomplete
                size="small"
                options={usuariosDisponibles}
                getOptionLabel={(u) => `${u.nombre} ${u.apellidos || ""}`.trim()}
                isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
                value={usuarioSeleccionado}
                onChange={(_, newValue) => setUsuarioId(newValue ? newValue.id : "")}
                loading={loadingData}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={esAdmin ? "Usuario" : "Usuario de mi equipo"}
                    placeholder="Escribe para buscar…"
                    required
                  />
                )}
                clearOnEscape
              />

              <Autocomplete
                size="small"
                options={centrosOrdenados}
                getOptionLabel={(c) => c.nombre || ""}
                isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
                value={centroSeleccionado}
                onChange={(_, newValue) => setCentroId(newValue ? newValue.id : "")}
                loading={loadingData}
                renderInput={(params) => (
                  <TextField {...params} label="Centro" placeholder="Escribe para buscar…" required />
                )}
                clearOnEscape
              />

              <TextField
                label="Inicio"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={inicio}
                onChange={(e) => setInicio(e.target.value)}
                required
              />

              <TextField
                label="Fin"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={fin}
                onChange={(e) => setFin(e.target.value)}
                required
              />

              <TextField
                label="Motivo o comentario"
                value={comentario}
                onChange={(e) => setComentario(e.target.value)}
                placeholder="Explica por qué se crea esta jornada manualmente"
                multiline
                minRows={3}
                sx={{ gridColumn: { xs: "auto", md: "1 / -1" } }}
              />

              <Stack
                direction="row"
                spacing={2}
                sx={{
                  gridColumn: "1 / -1",
                  justifyContent: "flex-end",
                  flexWrap: "wrap",
                  pt: 1,
                }}
              >
                <Button variant="outlined" onClick={limpiarFormulario} disabled={saving}>
                  Limpiar
                </Button>

                <Button type="submit" disabled={saving || loadingData}>
                  {saving ? "Creando..." : "Crear jornada"}
                </Button>
              </Stack>
            </Box>
          </Paper>
        </Box>
      </Stack>
    </Box>
  );
};